import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, Check, AlertCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

// Lazily load the Razorpay Checkout script exactly once.
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const BENEFITS = [
  {
    title: "Your Own Professional Site",
    body: "A clean, branded website that makes you look like a pro to every client.",
  },
  {
    title: "The “Pune Knowledge Hub”",
    body: "Stop searching through brochures. Instant access to hundreds of real-time project details across Pune’s hottest areas.",
  },
  {
    title: "AI-Powered WhatsApp Assistant",
    body: "When you get a floor plan or inquiry on WhatsApp, our system extracts the details and organises them for you automatically.",
  },
  {
    title: "Lead “Gatekeeper”",
    body: "Let the tool do the boring work of filtering out time-wasters, so you only talk to people who are actually ready to buy.",
  },
  {
    title: "Instant Financial Tools",
    body: "Need to show a client their EMI or Stamp Duty breakdown? Do it in seconds with built-in calculators.",
  },
  {
    title: "Tracking & Alerts",
    body: "See exactly what your clients are interested in and get notified the second a new lead comes in.",
  },
];

export default function Subscribe() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // If user is already active, bounce them.
  useEffect(() => {
    if (user?.subscription_status === "active") {
      navigate(user.onboarded ? "/dashboard" : "/onboarding");
    }
  }, [user, navigate]);

  const onCheckout = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay Checkout. Please check your connection and retry.");

      const { data } = await api.post("/billing/razorpay/create-subscription");
      if (!data?.subscription_id || !data?.key_id) {
        throw new Error("Razorpay is not configured yet. Please try again in a minute.");
      }

      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "INPropPortal",
        description: `Agent Monthly · ₹${data.amount_inr ?? 999}`,
        image: "/inpropportal-logo.png",
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        notes: { user_id: user?.user_id, plan: "agent_monthly_inr999" },
        theme: { color: "#0B1B33" },
        handler: () => {
          // Razorpay has authorised the mandate. Move to the confirmation
          // page which polls until the first charge lands.
          navigate(`/billing/success?subscription_id=${data.subscription_id}`);
        },
        modal: {
          ondismiss: () => {
            toast("Checkout closed. You can resume the same subscription any time.");
            setSubmitting(false);
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        const msg = resp?.error?.description || "Payment failed. Please try again.";
        toast.error(msg);
        setErr(msg);
        setSubmitting(false);
      });
      rzp.open();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.message || "Could not start checkout."));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sg-paper">
      <header className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src="/inpropportal-logo.png" alt="INPropPortal.com" className="h-11 w-auto" />
          </Link>
          <div className="text-xs font-mono text-sg-gold">STEP 2 OF 3 · SUBSCRIBE</div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="overline text-sg-gold-deep mb-3">CHOOSE YOUR PLAN</div>
        <h1 className="font-display text-5xl text-sg-navy max-w-3xl leading-tight">
          One plan. <span className="font-serif-italic text-sg-gold-deep">Everything an agent needs.</span>
        </h1>
        <p className="mt-4 text-sg-mute max-w-2xl">
          Single tier — built for Pune solo agents and small brokerages. Cancel any time from your dashboard.
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 bg-white border border-sg-line rounded-3xl p-8 shadow-sm" data-testid="subscribe-card">
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-xs text-sg-gold-deep tracking-wider">INPROPPORTAL · SUBSCRIPTION INCLUDES</span>
            </div>
            <div className="mt-2 flex items-end gap-3">
              <span className="font-display text-6xl text-sg-navy">₹999</span>
              <span className="text-sg-mute mb-2">/ month · GST inclusive</span>
            </div>
            <ul className="mt-8 space-y-4">
              {BENEFITS.map((b) => (
                <li key={b.title} className="flex items-start gap-3" data-testid={`subscribe-benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Check className="w-4 h-4 text-sg-gold mt-1.5 shrink-0" />
                  <div className="text-sm text-sg-navy leading-relaxed">
                    <strong className="text-sg-navy">{b.title}:</strong> <span className="text-sg-mute">{b.body}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-6 bg-sg-cream-soft border border-sg-line rounded-xl px-4 py-3 text-sm text-sg-navy">
              <strong className="text-sg-gold-deep">Close deals faster</strong> with our automations — and much more at just <strong>₹999 per month</strong>. Less than even a chai &amp; vada pav a day.
            </div>
            {err && (
              <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="subscribe-error">
                <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
              </div>
            )}
          </div>

          <aside className="lg:col-span-2 bg-sg-cream-soft border border-sg-line rounded-3xl p-7">
            <div className="overline text-sg-gold-deep">WHY ₹999?</div>
            <p className="mt-3 text-sm text-sg-navy">
              Less than one chai-and-vada-pav stall per day. Less than 0.1% of the typical Pune brokerage on a single ₹80 lakh deal.
            </p>
            <div className="mt-6 border-t border-sg-line pt-6 text-xs text-sg-mute leading-relaxed space-y-2">
              <p>· Signed in as <strong className="text-sg-navy">{user?.email}</strong></p>
              <p>· Switch account? <Link to="/login" className="border-b border-sg-gold text-sg-gold-deep">Sign out</Link></p>
              <p>· Need help? <a href="mailto:hello@inpropportal.com" className="border-b border-sg-gold text-sg-gold-deep">hello@inpropportal.com</a></p>
            </div>
          </aside>
        </div>

        {/* Full-width payment block — the single Razorpay checkout option */}
        <section className="mt-12 bg-white border border-sg-line rounded-3xl p-8 shadow-sm" data-testid="subscribe-payment-section">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <div className="overline text-sg-gold-deep">HOW YOU&apos;LL PAY</div>
              <h2 className="font-display text-3xl text-sg-navy mt-1">
                One secure checkout — <span className="font-serif-italic text-sg-gold-deep">every option in one place.</span>
              </h2>
            </div>
            <div
              className="inline-flex items-center gap-2 rounded-full bg-sg-cream-soft border border-sg-line px-4 py-2 text-sm text-sg-navy"
              data-testid="subscribe-tab-razorpay"
            >
              <CreditCard className="w-4 h-4 text-sg-gold-deep" />
              Card / Net Banking / UPI Scan
            </div>
          </div>

          <div className="max-w-2xl mx-auto py-8 text-center">
            <CreditCard className="w-10 h-10 text-sg-gold-deep mx-auto" />
            <p className="mt-4 text-sg-navy" data-testid="subscribe-billing-copy">
              Pay <strong>₹999 today</strong> to start, then <strong>₹999 on the 5th of every month</strong> for <strong>11 more months</strong>.
            </p>
            <p className="mt-2 text-sm text-sg-mute">
              Cancel any time from your dashboard — future auto-debits stop instantly.
            </p>
            <Button
              onClick={onCheckout}
              disabled={submitting}
              size="lg"
              className="mt-6 h-14 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white text-base px-8"
              data-testid="subscribe-cta"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Pay ₹999 &amp; Subscribe <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="mt-4 text-xs text-sg-mute">Powered by Razorpay · 100% secure · PCI-DSS certified</p>
          </div>
        </section>
      </main>
    </div>
  );
}
